import TableQueryState from "./table_query_state"

const coordinators = new WeakMap()
const HISTORY_SCROLL_KEY = "lucantoTableScroll"
const LOADING_DELAY_MS = 150

const coordinatorFor = (documentObject, frameId) => {
  let frames = coordinators.get(documentObject)
  if (!frames) {
    frames = new Map()
    coordinators.set(documentObject, frames)
  }
  if (!frames.has(frameId)) {
    frames.set(frameId, {
      revision: 0,
      pendingFrameLoad: null,
      pendingAnimationFrame: null,
      pendingFetchRequest: null,
      pendingNavigationResult: null,
      lastSuccessfulLocation: null,
      lastSuccessfulScroll: null,
      scrollByLocation: new Map(),
      connections: 0,
      loadingTimer: null
    })
  }
  return frames.get(frameId)
}

export default class TableFrameNavigator {
  constructor({
    frameId,
    tableKey = null,
    onPopState,
    onNavigate,
    windowObject = window,
    documentObject = document,
    turboVisit = (...args) => windowObject.Turbo?.visit(...args)
  }) {
    this.frameId = frameId
    this.tableKey = tableKey
    this.onPopState = onPopState
    this.onNavigate = onNavigate
    this.window = windowObject
    this.document = documentObject
    this.turboVisit = turboVisit
    this.lifecycle = coordinatorFor(documentObject, frameId)
    this.lifecycle.connections += 1
    this.disconnected = false
    if (!this.lifecycle.pendingNavigationResult) {
      const locationChanged = this.lifecycle.lastSuccessfulLocation?.url !== this.window.location.href
      this.lifecycle.lastSuccessfulLocation = {
        url: this.window.location.href,
        state: this.window.history.state
      }
      if (locationChanged) this.lifecycle.lastSuccessfulScroll = null
    }
    this.searchTimeout = null
    this.boundPopState = this.#handlePopState.bind(this)
    this.boundBeforeFetchRequest = this.#handleBeforeFetchRequest.bind(this)
    this.boundNavigationStart = this.#handleNavigationStart.bind(this)
    this.boundTableScroll = this.#handleTableScroll.bind(this)
    this.boundBeforeCache = () => this.#clearLoadingState()
    if (this.onPopState) this.window.addEventListener("popstate", this.boundPopState)
    this.document.addEventListener("lucanto:table-navigation-start", this.boundNavigationStart)
    this.document.addEventListener("scroll", this.boundTableScroll, true)
    this.document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    this.#frame()?.addEventListener("turbo:before-fetch-request", this.boundBeforeFetchRequest)
    if (!this.lifecycle.lastSuccessfulScroll) this.#recordCurrentTableScroll()
  }

  disconnect() {
    if (this.disconnected) return
    this.disconnected = true
    if (this.onPopState) this.window.removeEventListener("popstate", this.boundPopState)
    this.document.removeEventListener("lucanto:table-navigation-start", this.boundNavigationStart)
    this.document.removeEventListener("scroll", this.boundTableScroll, true)
    this.document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.#frame()?.removeEventListener("turbo:before-fetch-request", this.boundBeforeFetchRequest)
    this.#clearSearchTimeout()
    this.lifecycle.connections = Math.max(0, this.lifecycle.connections - 1)
    if (this.lifecycle.connections === 0) {
      this.lifecycle.lastSuccessfulLocation = null
      this.lifecycle.lastSuccessfulScroll = null
      this.#clearLoadingState()
    }
  }

  navigate(state, options = {}) {
    const {
      history = "push",
      reason = "navigation",
      preserveTableScroll = false,
      preserveScroll = null,
      restoreFocusSelector = null,
      baseUrl = `${this.window.location.origin}${this.window.location.pathname}`
    } = options
    const queryState = state instanceof TableQueryState ? state : new TableQueryState(state)
    const url = queryState.toUrl(baseUrl)
    this.#persistCurrentScrollInHistory()
    const priorLocation = { ...this.lifecycle.lastSuccessfulLocation }
    this.#announceNavigationStart()
    this.#clearSearchTimeout()
    this.#invalidateLifecycle()
    const revision = this.lifecycle.revision
    const frame = this.#frame()
    this.#emitNavigationState({ reason, revision, result: "started" })
    this.#scheduleLoadingState(frame, reason, queryState)

    if (frame) {
      this.#observeNavigationResult(frame, revision, reason, { priorLocation })
      const scrollPolicy = preserveScroll || (preserveTableScroll ? { x: true, y: true } : null)
      if (scrollPolicy || restoreFocusSelector) {
        this.#captureAndRestoreState(frame, revision, scrollPolicy, restoreFocusSelector)
      }

      if (history === "none") {
        frame.setAttribute("src", url)
      } else {
        this.turboVisit(url, { frame: this.frameId, action: history === "replace" ? "replace" : "advance" })
      }
    } else {
      this.#finishNavigation({ reason, revision, result: "failed", priorLocation })
    }

    this.onNavigate?.({ queryState, url, reason, revision })
    return { queryState, url, revision }
  }

  scheduleSearch(state, options = {}) {
    this.#announceNavigationStart()
    this.#clearSearchTimeout()
    this.#invalidateLifecycle()
    this.searchTimeout = this.window.setTimeout(() => {
      this.searchTimeout = null
      this.navigate(state, { ...options, history: "replace", reason: options.reason || "search" })
    }, options.delay ?? 250)
  }

  #handlePopState() {
    this.#announceNavigationStart()
    this.#clearSearchTimeout()
    this.#invalidateLifecycle()
    const revision = this.lifecycle.revision
    const frame = this.#frame()
    const locationScroll = this.lifecycle.scrollByLocation.get(this.window.location.href)
    const historyScroll = this.window.history.state?.[HISTORY_SCROLL_KEY]?.[this.frameId]
    this.#emitNavigationState({ reason: "popstate", revision, result: "started" })
    if (frame) {
      this.#observeNavigationResult(frame, revision, "popstate", {
        restoreOnPageLoad: true,
        priorLocation: { ...this.lifecycle.lastSuccessfulLocation }
      })
      this.#captureAndRestoreState(frame, revision, { x: true, y: false }, null, {
        restoreOnPageLoad: true,
        capturedScroll: locationScroll || historyScroll || this.lifecycle.lastSuccessfulScroll
      })
    }
    this.onPopState?.(new TableQueryState(this.window.location.search))
  }

  #captureAndRestoreState(
    frame,
    revision,
    scrollPolicy,
    restoreFocusSelector,
    { restoreOnPageLoad = false, capturedScroll = null } = {}
  ) {
    const currentScroller = frame.querySelector(".documents-table-scroll")
    const scrollLeft = Math.max(0, capturedScroll?.x ?? currentScroller?.scrollLeft ?? 0)
    const scrollTop = Math.max(0, capturedScroll?.y ?? currentScroller?.scrollTop ?? 0)
    const restore = (replacementFrame) => {
      if (!replacementFrame || revision !== this.lifecycle.revision) return
      this.#clearPendingFrameLoad()
      this.#restoreState(replacementFrame, scrollLeft, scrollTop, scrollPolicy, restoreFocusSelector)
      this.lifecycle.pendingAnimationFrame = this.window.requestAnimationFrame(() => {
        if (revision !== this.lifecycle.revision) {
          this.lifecycle.pendingAnimationFrame = null
          return
        }
        this.#restoreState(replacementFrame, scrollLeft, scrollTop, scrollPolicy, restoreFocusSelector)
        this.lifecycle.pendingAnimationFrame = this.window.requestAnimationFrame(() => {
          this.lifecycle.pendingAnimationFrame = null
          if (revision === this.lifecycle.revision) {
            this.#restoreState(replacementFrame, scrollLeft, scrollTop, scrollPolicy, restoreFocusSelector)
          }
        })
      })
    }
    const handler = (event) => {
      if (event.target !== frame) return
      restore(frame)
    }
    const pageLoadHandler = () => restore(this.#frame())

    frame.addEventListener("turbo:frame-load", handler)
    if (restoreOnPageLoad) this.document.addEventListener("turbo:load", pageLoadHandler)
    this.lifecycle.pendingFrameLoad = { frame, handler, pageLoadHandler: restoreOnPageLoad ? pageLoadHandler : null }
  }

  #restoreState(frame, scrollLeft, scrollTop, scrollPolicy, restoreFocusSelector) {
    const scroller = frame.querySelector(".documents-table-scroll")
    if (scroller && scrollPolicy) {
      const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
      const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight)
      scroller.scrollLeft = scrollPolicy.x ? Math.min(scrollLeft, maxLeft) : 0
      scroller.scrollTop = scrollPolicy.y ? Math.min(scrollTop, maxTop) : 0
      const header = frame.querySelector('[data-table-interactions-target~="headerViewport"]')
      if (header) header.scrollLeft = scroller.scrollLeft
      scroller.dispatchEvent(new this.window.Event("scroll"))
    }
    if (restoreFocusSelector) {
      // A selector list ("#letter_all, #contacts-letter-button") names
      // alternatives; the first one that is actually rendered wins.
      const candidates = [...frame.querySelectorAll(restoreFocusSelector), ...this.document.querySelectorAll(restoreFocusSelector)]
      const focusTarget = candidates.find((el) => el.offsetParent !== null || el === this.document.activeElement) || candidates[0]
      if (!focusTarget) return
      focusTarget.focus({ preventScroll: true })
      // A re-rendered text field starts with the caret at position 0; the
      // user was typing at the end. (email/number inputs throw on selection.)
      if (typeof focusTarget.setSelectionRange === "function" && /^(text|search|url|tel|password)$/.test(focusTarget.type || "")) {
        const end = focusTarget.value.length
        focusTarget.setSelectionRange(end, end)
      }
    }
  }

  #invalidateLifecycle() {
    this.lifecycle.revision += 1
    this.lifecycle.pendingFetchRequest?.cancel?.()
    this.lifecycle.pendingFetchRequest = null
    this.#clearFrameRestore()
    this.#clearNavigationResult()
  }

  #clearFrameRestore() {
    this.#clearPendingFrameLoad()
    if (this.lifecycle.pendingAnimationFrame !== null) {
      this.window.cancelAnimationFrame(this.lifecycle.pendingAnimationFrame)
      this.lifecycle.pendingAnimationFrame = null
    }
  }

  #clearPendingFrameLoad() {
    if (this.lifecycle.pendingFrameLoad) {
      const { frame, handler, pageLoadHandler } = this.lifecycle.pendingFrameLoad
      frame.removeEventListener("turbo:frame-load", handler)
      if (pageLoadHandler) this.document.removeEventListener("turbo:load", pageLoadHandler)
      this.lifecycle.pendingFrameLoad = null
    }
  }

  #clearSearchTimeout() {
    if (this.searchTimeout === null) return
    this.window.clearTimeout(this.searchTimeout)
    this.searchTimeout = null
  }

  #observeNavigationResult(frame, revision, reason, { restoreOnPageLoad = false, priorLocation = null } = {}) {
    const finish = (result) => (event) => {
      if (event.target !== frame || revision !== this.lifecycle.revision) return
      if (result === "failed" && event.type === "turbo:frame-missing") event.preventDefault?.()
      this.#finishNavigation({ reason, revision, result, priorLocation })
    }
    const loadHandler = finish("loaded")
    const errorHandler = finish("failed")
    const missingHandler = finish("failed")
    const responseHandler = (event) => {
      if (event.target !== frame || revision !== this.lifecycle.revision) return
      if (event.detail?.fetchResponse?.succeeded === false) {
        event.preventDefault?.()
        this.#finishNavigation({ reason, revision, result: "failed", priorLocation })
      }
    }
    const pageLoadHandler = () => {
      if (revision !== this.lifecycle.revision) return
      this.#finishNavigation({ reason, revision, result: "loaded", priorLocation })
    }
    frame.addEventListener("turbo:frame-load", loadHandler)
    frame.addEventListener("turbo:fetch-request-error", errorHandler)
    frame.addEventListener("turbo:frame-missing", missingHandler)
    frame.addEventListener("turbo:before-fetch-response", responseHandler)
    if (restoreOnPageLoad) this.document.addEventListener("turbo:load", pageLoadHandler)
    this.lifecycle.pendingNavigationResult = {
      frame,
      loadHandler,
      errorHandler,
      missingHandler,
      responseHandler,
      pageLoadHandler: restoreOnPageLoad ? pageLoadHandler : null
    }
  }

  #clearNavigationResult() {
    const pending = this.lifecycle.pendingNavigationResult
    if (!pending) return
    pending.frame.removeEventListener("turbo:frame-load", pending.loadHandler)
    pending.frame.removeEventListener("turbo:fetch-request-error", pending.errorHandler)
    pending.frame.removeEventListener("turbo:frame-missing", pending.missingHandler)
    pending.frame.removeEventListener("turbo:before-fetch-response", pending.responseHandler)
    if (pending.pageLoadHandler) this.document.removeEventListener("turbo:load", pending.pageLoadHandler)
    this.lifecycle.pendingNavigationResult = null
  }

  #finishNavigation({ reason, revision, result, priorLocation }) {
    if (revision !== this.lifecycle.revision) return
    this.#clearLoadingState()
    this.#clearNavigationResult()
    this.#emitNavigationState({ reason, revision, result })
    if (result !== "failed") {
      this.lifecycle.lastSuccessfulLocation = {
        url: this.window.location.href,
        state: this.window.history.state
      }
      this.#recordCurrentTableScroll({ force: true })
      return
    }

    this.#clearFrameRestore()
    if (priorLocation?.url) {
      this.window.history.replaceState(priorLocation.state, "", priorLocation.url)
    }
    const detail = { tableKey: this.tableKey, frameId: this.frameId, reason, revision, result: "failed" }
    this.document.dispatchEvent(new this.window.CustomEvent("lucanto:table-navigation-rollback", { detail }))
    this.document.dispatchEvent(
      new this.window.CustomEvent("lucanto:table-navigation-feedback", {
        detail: { tableKey: this.tableKey, frameId: this.frameId, type: "error", code: "navigation_failed" }
      })
    )
  }

  #emitNavigationState({ reason, revision, result }) {
    this.document.dispatchEvent(
      new this.window.CustomEvent("lucanto:table-query-changed", {
        detail: { tableKey: this.tableKey, frameId: this.frameId, reason, revision, result }
      })
    )
  }

  #announceNavigationStart() {
    this.document.dispatchEvent(
      new this.window.CustomEvent("lucanto:table-navigation-start", {
        detail: { frameId: this.frameId, source: this }
      })
    )
  }

  #frame() {
    return this.document.getElementById(this.frameId)
  }

  #handleBeforeFetchRequest(event) {
    if (event.target !== this.#frame()) return
    this.lifecycle.pendingFetchRequest = event.detail?.fetchRequest || null
  }

  #handleNavigationStart(event) {
    if (event.detail?.frameId !== this.frameId || event.detail?.source === this) return
    this.#clearSearchTimeout()
  }

  #handleTableScroll(event) {
    if (!event.target?.matches?.(".documents-table-scroll")) return
    if (!this.#frame()?.contains(event.target)) return
    if (this.lifecycle.lastSuccessfulLocation?.url !== this.window.location.href) return

    this.lifecycle.lastSuccessfulScroll = {
      x: Math.max(0, event.target.scrollLeft || 0),
      y: Math.max(0, event.target.scrollTop || 0)
    }
    this.lifecycle.scrollByLocation.set(this.lifecycle.lastSuccessfulLocation.url, {
      ...this.lifecycle.lastSuccessfulScroll
    })
  }

  #recordCurrentTableScroll({ force = false } = {}) {
    if (!force && this.lifecycle.lastSuccessfulScroll) return
    const scroller = this.#frame()?.querySelector(".documents-table-scroll")
    if (!scroller) return
    this.lifecycle.lastSuccessfulScroll = {
      x: Math.max(0, scroller.scrollLeft || 0),
      y: Math.max(0, scroller.scrollTop || 0)
    }
    if (this.lifecycle.lastSuccessfulLocation?.url) {
      this.lifecycle.scrollByLocation.set(this.lifecycle.lastSuccessfulLocation.url, {
        ...this.lifecycle.lastSuccessfulScroll
      })
    }
  }

  #persistCurrentScrollInHistory() {
    const scroller = this.#frame()?.querySelector(".documents-table-scroll")
    if (!scroller) return
    const currentState = this.window.history.state
    const nextState = currentState && typeof currentState === "object" ? { ...currentState } : {}
    const scrollByFrame = { ...(nextState[HISTORY_SCROLL_KEY] || {}) }
    scrollByFrame[this.frameId] = {
      x: Math.max(0, scroller.scrollLeft || 0),
      y: Math.max(0, scroller.scrollTop || 0)
    }
    nextState[HISTORY_SCROLL_KEY] = scrollByFrame
    this.window.history.replaceState(nextState, "", this.window.location.href)
    this.lifecycle.scrollByLocation.set(this.window.location.href, { ...scrollByFrame[this.frameId] })
    if (this.lifecycle.lastSuccessfulLocation?.url === this.window.location.href) {
      this.lifecycle.lastSuccessfulLocation.state = nextState
      this.lifecycle.lastSuccessfulScroll = { ...scrollByFrame[this.frameId] }
    }
  }

  #scheduleLoadingState(frame, reason, _queryState) {
    this.#clearLoadingState()
    if (!frame) return
    this.lifecycle.loadingTimer = this.window.setTimeout(() => {
      this.lifecycle.loadingTimer = null
      frame.setAttribute("data-table-loading", "true")
      const status = frame.querySelector(".table-footer__status")
      const controller = frame.querySelector("[data-table-navigation-loading-text-value]")
      if (!status || !controller) return
      const query = reason === "search" ? frame.querySelector("[name='search']")?.value?.trim() : ""
      const template = query
        ? controller.dataset.tableNavigationSearchingTextValue
        : controller.dataset.tableNavigationLoadingTextValue
      status.textContent = (template || "").replace("%{query}", query)
    }, LOADING_DELAY_MS)
  }

  #clearLoadingState() {
    if (this.lifecycle.loadingTimer) {
      this.window.clearTimeout(this.lifecycle.loadingTimer)
      this.lifecycle.loadingTimer = null
    }
    const frame = this.#frame()
    if (!frame) return
    frame.removeAttribute("data-table-loading")
    const status = frame.querySelector(".table-footer__status")
    if (status) status.textContent = ""
  }
}
